import React, { useEffect } from 'react';

// Shimmer animation styles - added via useEffect
const useShimmerStyles = () => {
  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('skeleton-shimmer-styles')) {
      const style = document.createElement('style');
      style.id = 'skeleton-shimmer-styles';
      style.textContent = `
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .shimmer {
          animation: shimmer 1.5s infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
};

// Basic shimmer box component
export const ShimmerBox = ({ width, height, borderRadius = '12px', className = '' }) => {
  useShimmerStyles();

  return (
    <div
      className={`shimmer bg-black/8 dark:bg-white/8 ${className}`}
      style={{
        width: width || '100%',
        height: height || '20px',
        borderRadius,
        backgroundImage: 'linear-gradient(90deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.12) 50%, rgba(0, 0, 0, 0.08) 100%)',
        backgroundSize: '200% 100%',
      }}
    />
  );
};

// Table skeleton component
export const TableSkeleton = ({ rows = 8, columns = 5, showHeader = true }) => {
  useShimmerStyles();

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse">
        {showHeader && (
          <thead>
            <tr className="border-b border-gray-200 dark:border-[oklch(0.33_0.022_264)]">
              {[...Array(columns)].map((_, index) => (
                <th key={index} className="px-4 py-3 text-left">
                  <ShimmerBox width="60%" height="16px" />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {[...Array(rows)].map((_, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 0 ? 'bg-white dark:bg-[oklch(0.26_0.028_264/0.3)]' : 'bg-gray-50 dark:bg-[oklch(0.26_0.028_264/0.5)]'}
            >
              {[...Array(columns)].map((_, colIndex) => (
                <td key={colIndex} className="px-4 py-3">
                  <ShimmerBox width={colIndex === 0 ? "70%" : "60%"} height="14px" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Card skeleton component (for grid layouts)
export const CardSkeleton = ({ count = 6, showImage = true, showTitle = true, showDescription = true }) => {
  useShimmerStyles();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(count)].map((_, index) => (
        <div
          key={index}
          className="p-4 rounded-lg border border-gray-200 dark:border-[oklch(0.33_0.022_264)] bg-white dark:bg-[oklch(0.26_0.028_264)] shadow-sm"
        >
          {showImage && (
            <ShimmerBox width="100%" height="200px" borderRadius="8px" className="mb-4" />
          )}
          {showTitle && (
            <ShimmerBox width="80%" height="20px" className="mb-3" />
          )}
          {showDescription && (
            <div className="space-y-2">
              <ShimmerBox width="100%" height="14px" />
              <ShimmerBox width="95%" height="14px" />
              <ShimmerBox width="85%" height="14px" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Form skeleton component
export const FormSkeleton = ({ fields = 5 }) => {
  useShimmerStyles();

  return (
    <div className="space-y-6 p-6 bg-white dark:bg-[oklch(0.26_0.028_264)] rounded-lg border border-gray-200 dark:border-[oklch(0.33_0.022_264)]">
      {[...Array(fields)].map((_, index) => (
        <div key={index} className="space-y-2">
          <ShimmerBox width="30%" height="16px" />
          <ShimmerBox width="100%" height="40px" borderRadius="6px" />
        </div>
      ))}
      <div className="flex gap-3 pt-4">
        <ShimmerBox width="100px" height="40px" borderRadius="6px" />
        <ShimmerBox width="100px" height="40px" borderRadius="6px" />
      </div>
    </div>
  );
};

// List skeleton component (for email threads, notifications, etc.)
export const ListSkeleton = ({ items = 5, showAvatar = true, showTitle = true, showDescription = true }) => {
  useShimmerStyles();

  return (
    <div className="space-y-4">
      {[...Array(items)].map((_, index) => (
        <div
          key={index}
          className="p-4 rounded-lg border border-gray-200 dark:border-[oklch(0.33_0.022_264)] bg-white dark:bg-[oklch(0.26_0.028_264)]"
        >
          <div className="flex items-start gap-3">
            {showAvatar && (
              <ShimmerBox width="40px" height="40px" borderRadius="50%" />
            )}
            <div className="flex-1 space-y-2">
              {showTitle && (
                <div className="flex items-center justify-between">
                  <ShimmerBox width="40%" height="16px" />
                  <ShimmerBox width="20%" height="14px" />
                </div>
              )}
              {showDescription && (
                <div className="space-y-2">
                  <ShimmerBox width="100%" height="14px" />
                  <ShimmerBox width="95%" height="14px" />
                  <ShimmerBox width="85%" height="14px" />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Email thread skeleton (specialized list skeleton)
export const EmailThreadSkeleton = ({ messages = 3 }) => {
  useShimmerStyles();

  return (
    <div className="space-y-4">
      {[...Array(messages)].map((_, index) => (
        <div
          key={index}
          className="p-4 rounded-lg bg-white/40 dark:bg-[oklch(0.26_0.028_264/0.4)] backdrop-blur-[8px] border border-white/25 dark:border-[oklch(0.33_0.022_264/0.5)]"
          style={{
            WebkitBackdropFilter: 'blur(8px)'
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <ShimmerBox width="40px" height="40px" borderRadius="50%" />
            <div className="flex-1 space-y-2">
              <ShimmerBox width="60%" height="16px" />
              <ShimmerBox width="40%" height="12px" />
            </div>
          </div>
          <div className="space-y-2 mt-3">
            <ShimmerBox width="100%" height="14px" />
            <ShimmerBox width="95%" height="14px" />
            <ShimmerBox width="85%" height="14px" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default {
  ShimmerBox,
  TableSkeleton,
  CardSkeleton,
  FormSkeleton,
  ListSkeleton,
  EmailThreadSkeleton,
};

